import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { X, Eye, Maximize2, Minimize2 } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import PerguntaUnicaEscolha from '../dynamic/PerguntaUnicaEscolha';
import PerguntaMultiplaEscolha from '../dynamic/PerguntaMultiplaEscolha';
import PerguntaAberta from '../dynamic/PerguntaAberta';

const COMPONENTE_POR_TIPO = {
  unica_escolha: PerguntaUnicaEscolha,
  multipla_escolha: PerguntaMultiplaEscolha,
  aberta: PerguntaAberta
};

// Preview grande em modal (não navega pra página própria) de como a enquete
// aparece pro público, com um botão de expandir pra tela cheia. Reaproveita
// os mesmos componentes de pergunta do formulário público
// (components/dynamic/) — nunca envia nada, o botão de enviar fica desabilitado.
const PreviewModal = ({ enqueteId, onFechar }) => {
  const [enquete, setEnquete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telaCheia, setTelaCheia] = useState(false);
  const formMethods = useForm();

  useEffect(() => {
    adminEnquetesAPI.obter(enqueteId)
      .then(setEnquete)
      .catch(() => toast.error('Erro ao carregar preview'))
      .finally(() => setLoading(false));
  }, [enqueteId]);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onFechar();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onFechar]);

  const perguntasAtivas = (enquete?.perguntas || [])
    .filter((p) => p.ativa)
    .sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onFechar} />
      <div
        className={`relative w-full bg-corumba-light shadow-2xl animate-modal-in flex flex-col ${
          telaCheia
            ? 'h-screen max-h-screen rounded-none'
            : 'max-w-3xl max-h-[92vh] sm:max-h-[88vh] rounded-t-2xl sm:rounded-2xl overflow-hidden'
        }`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/10 bg-primary-600 text-white px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="font-semibold text-sm sm:text-base truncate pr-3 flex items-center gap-2">
              <Eye className="w-4 h-4 flex-shrink-0" /> Ver Preview{enquete ? `: ${enquete.titulo}` : ''}
            </h2>
            <p className="text-xs text-primary-100 mt-0.5 hidden sm:block">
              Prévia de como o formulário aparece pro público — nada aqui é enviado ou contado como voto.
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setTelaCheia((v) => !v)}
              className="w-8 h-8 flex items-center justify-center rounded-full text-white bg-white/20 border border-white/30 hover:bg-white/30 transition-colors"
              aria-label={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
              title={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
            >
              {telaCheia ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onFechar}
              className="w-8 h-8 flex items-center justify-center rounded-full text-white bg-white/20 border border-white/30 hover:bg-white/30 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && (
            <div className="flex justify-center py-12"><div className="loading-spinner" /></div>
          )}

          {!loading && !enquete && (
            <p className="text-gray-500 text-center py-12">Enquete não encontrada.</p>
          )}

          {!loading && enquete && (
            <div className="max-w-2xl mx-auto">
              <FormProvider {...formMethods}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    toast('Pré-visualização: nenhum voto é registrado aqui.', { icon: 'ℹ️' });
                  }}
                  className="space-y-6"
                >
                  {perguntasAtivas.length === 0 && (
                    <p className="text-gray-500 text-center py-8">Nenhuma pergunta ativa ainda.</p>
                  )}

                  {perguntasAtivas.map((pergunta, index) => {
                    const Componente = COMPONENTE_POR_TIPO[pergunta.tipo];
                    if (!Componente) return null;
                    return <Componente key={pergunta.id} pergunta={pergunta} indice={index + 1} />;
                  })}

                  {perguntasAtivas.length > 0 && (
                    <div className="card text-center">
                      <button
                        type="submit"
                        className="bg-gray-300 text-gray-600 font-medium py-2 px-6 rounded-lg cursor-not-allowed"
                      >
                        Enviar (desabilitado na prévia)
                      </button>
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex justify-center border-t border-gray-100 px-4 sm:px-6 py-3.5 sm:py-4">
          <button onClick={onFechar} className="btn-primary px-8">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
